import * as React from 'react';

export function useCommitOnEvents<T>({
  value,
  rawValue,
  parse,
  equals = Object.is,
  onChange,
  inputRef,
}: {
  value: T;
  rawValue: string;
  parse: (raw: string) => T;
  equals?: (a: T, b: T) => boolean;
  onChange?: (value: T) => void;
  inputRef: React.RefObject<HTMLElement | null>;
}) {
  const latestRef = React.useRef(rawValue);
  latestRef.current = rawValue;
  const committedRef = React.useRef<T>(value);
  committedRef.current = value;

  const commit = React.useCallback(() => {
    const next = parse(latestRef.current);
    if (!equals(next, committedRef.current)) {
      committedRef.current = next;
      onChange?.(next);
    }
  }, [parse, equals, onChange]);

  const handleBlur = React.useCallback(() => commit(), [commit]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
        inputRef.current?.blur();
      } else if (e.key === 'Tab') {
        commit();
      }
    },
    [commit, inputRef],
  );

  return { commit, handleBlur, handleKeyDown };
}
